import React, { useState, useEffect, useRef } from "react";
import { networkManager } from "../../utils/networkManager";
import "./NetworkStatusModal.css";

/**
 * Middle Pop-up for No Network / No Internet Connection
 * Designed specifically for eTour Web App Theme, Dashboard, and Spacing.
 * Renders in center of screen with backdrop blur, retry action, auto-reconnect,
 * and clean diagnostic tips.
 */
export const NetworkStatusModal = () => {
  const [networkState, setNetworkState] = useState(networkManager.getState());
  const [isDismissed, setIsDismissed] = useState(false);
  const [showRestoredAnimation, setShowRestoredAnimation] = useState(false);
  const autoCloseTimerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = networkManager.subscribe((state) => {
      setNetworkState(state);

      if (!state.isOnline) {
        // When going offline, re-open if not explicitly dismissed in this session
        setShowRestoredAnimation(false);
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = null;
        }
      } else if (state.isOnline && state.wasOffline) {
        // Internet just recovered! Show restored success state then smoothly close
        setShowRestoredAnimation(true);
        setIsDismissed(false);

        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
        }
        autoCloseTimerRef.current = setTimeout(() => {
          setShowRestoredAnimation(false);
          networkManager.clearWasOffline();
        }, 1800);
      }
    });

    return () => {
      unsubscribe();
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const handleReopen = () => {
    setIsDismissed(false);
    networkManager.checkConnectivity();
  };

  // If online and not in the brief restored animation, do not render modal
  const isCurrentlyOffline = !networkState.isOnline;
  const isVisible = (isCurrentlyOffline || showRestoredAnimation) && !isDismissed;

  // Keyboard shortcut listener (Escape or Enter to dismiss)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible) return;
      if (e.key === "Escape" || e.key === "Enter") {
        handleDismiss();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible]);

  // If user dismissed modal while offline, show subtle floating pill
  if (isCurrentlyOffline && isDismissed) {
    return (
      <div
        className="etour-network-floating-pill"
        onClick={handleReopen}
        role="button"
        tabIndex={0}
        title="Internet connection is offline. Click to view."
      >
        <span className="etour-network-floating-dot" />
        <span>You are offline</span>
      </div>
    );
  }

  if (!isVisible) {
    return null;
  }

  const isRestored = showRestoredAnimation && networkState.isOnline;

  return (
    <div
      className="etour-network-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="etour-net-title"
      aria-describedby="etour-net-desc"
    >
      <div className={`etour-network-modal ${isRestored ? "is-restored" : ""}`}>
        {/* Top Accent Gradient Line */}
        <div className="etour-network-accent-bar" />

        {/* Close Button */}
        {!isRestored && (
          <button
            type="button"
            className="etour-network-close-btn"
            onClick={handleDismiss}
            aria-label="Dismiss offline alert"
          >
            &times;
          </button>
        )}

        {/* Central State Icon */}
        <div className={`etour-network-icon-halo ${isRestored ? "restored" : "offline"}`}>
          {isRestored ? (
            /* Clean SVG Wifi Connected / Checkmark */
            <svg
              className="etour-network-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <circle cx="12" cy="20" r="1.5" fill="currentColor" />
            </svg>
          ) : (
            /* Clean SVG Wifi Off / Disconnected */
            <svg
              className="etour-network-icon-svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
              <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" strokeWidth="3" />
            </svg>
          )}
        </div>

        {/* Title */}
        <h3 id="etour-net-title" className="etour-network-title">
          {isRestored
            ? "Back Online!"
            : "No Network Connection"}
        </h3>

        {/* Message */}
        <p id="etour-net-desc" className="etour-network-message">
          {isRestored
            ? "Internet connection has been successfully restored. You can continue working safely."
            : "We're unable to reach the internet. Changes and data submissions will pause until your connection is restored."}
        </p>

        {/* Diagnostic checklist (hidden during brief restore confirmation) */}
        {!isRestored && (
          <div className="etour-network-tips-box">
            <div className="etour-network-tips-header">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" strokeWidth="3" />
              </svg>
              Quick Checks
            </div>
            <ul className="etour-network-tips-list">
              <li className="etour-network-tips-item">Check your Wi-Fi router or Ethernet cable</li>
              <li className="etour-network-tips-item">Verify if your device has an active internet plan</li>
              <li className="etour-network-tips-item">The app will automatically reconnect once online</li>
            </ul>
          </div>
        )}

        {/* Action Button */}
        {!isRestored && (
          <div className="etour-network-actions">
            <button
              type="button"
              className="etour-network-btn-dismiss"
              onClick={handleDismiss}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkStatusModal;
